import { useEffect, useState } from 'react';
import api from '../api';
import { parseCapabilityResponse } from './externalIntegrationsCapability';

/**
 * Whether the signed-in user's tenant may install/configure external
 * integrations (Slack, Teams, Jira…). Stemmler only — see Task 0.
 *
 * The backend is the authority; this is only what decides whether the UI
 * bothers to render. Hiding a page is a courtesy, not a security control: every
 * integration endpoint enforces the same flag server-side, so a user who
 * un-hides the page by hand still gets a 403 with an audit row.
 *
 * FAILS CLOSED. A network error, a 401, a malformed body — all resolve to
 * `false`. The alternative (treat unknown as allowed) would flash an
 * Integrations page at client-tenant users during any backend hiccup.
 *
 * The answer is cached for the page session because the nav renders on every
 * route and the value cannot change without a new login: the flag lives on the
 * tenant, and the tenant is fixed for the lifetime of the session.
 */

let cached: Promise<boolean> | null = null;

async function fetchCapability(): Promise<boolean> {
  try {
    const res = await api.get('/api/integrations/capabilities');
    return parseCapabilityResponse(res.data);
  } catch {
    return false;
  }
}

/** Drop the cached answer — call on logout / tenant switch. */
export const resetExternalIntegrationsCache = (): void => {
  cached = null;
};

export interface ExternalIntegrationsState {
  /** True only on an explicit yes from the server. */
  allowed: boolean;
  /** True until the first answer arrives. Render nothing while this is true. */
  loading: boolean;
}

export function useExternalIntegrations(): ExternalIntegrationsState {
  const [state, setState] = useState<ExternalIntegrationsState>({
    allowed: false,
    loading: true,
  });

  useEffect(() => {
    let active = true;
    if (!cached) cached = fetchCapability();
    cached.then((allowed) => {
      if (active) setState({ allowed, loading: false });
    });
    return () => {
      active = false;
    };
  }, []);

  return state;
}

export default useExternalIntegrations;
